import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Sparkles, Globe, CalendarCheck, ShoppingBag, Palette,
  Layout, Smartphone, Image as ImageIcon, FileText, Megaphone, Package, Check,
  Croissant, Building2, Scissors, Flower2, Dumbbell, Hammer, Store, Quote, Star
} from "lucide-react";
import { motion } from "framer-motion";
import { api, trackView, resolveImg } from "../lib/api";
import SEO from "../components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.06, duration: 0.6, ease: [0.21, 0.47, 0.32, 0.98] },
  }),
};

function StatNumber(_) { return null; }
// Kept as no-op for now in case re-enabled later. (Stats moved to /admin only.)


const services = {
  sites: [
    { icon: Layout, title: "Site vitrine", desc: "Présentation professionnelle, claire et convaincante." },
    { icon: CalendarCheck, title: "Prise de rendez-vous", desc: "Système de réservation intégré 24/7." },
    { icon: ShoppingBag, title: "Click & Collect", desc: "Vente en ligne avec retrait en boutique." },
    { icon: Package, title: "C&C + Rendez-vous", desc: "Solution hybride pour activités mixtes." },
    { icon: Globe, title: "Refonte de site", desc: "Modernisation complète d'un site existant." },
    { icon: Smartphone, title: "Optimisation mobile", desc: "Performance et UX parfaites sur mobile." },
  ],
  identite: [
    { icon: Sparkles, title: "Création de logo", desc: "Identité de marque forte et mémorable." },
    { icon: Palette, title: "Charte graphique", desc: "Système visuel cohérent et durable." },
    { icon: FileText, title: "Cartes de visite", desc: "Premium, élégantes, à votre image." },
    { icon: Megaphone, title: "Flyers & affiches", desc: "Communication imprimée percutante." },
    { icon: ImageIcon, title: "Bannières réseaux", desc: "Visuels optimisés Instagram, LinkedIn, Facebook." },
    { icon: Package, title: "Pack complet", desc: "Identité visuelle 360° clé en main." },
  ],
};

const industries = [
  { icon: Croissant, name: "Boulangerie", desc: "Vitrine + Click & Collect" },
  { icon: Building2, name: "Immobilier", desc: "Annonces + Prise de RDV" },
  { icon: Scissors, name: "Barber / Coiffeur", desc: "Réservation en ligne" },
  { icon: Flower2, name: "Fleuriste", desc: "Boutique + livraison locale" },
  { icon: Dumbbell, name: "Salle de sport", desc: "Cours + abonnements" },
  { icon: Hammer, name: "Artisan", desc: "Portfolio + demande de devis" },
  { icon: Store, name: "Commerce local", desc: "Vitrine + Click & Collect" },
];

const testimonials = [
  { name: "Sophie L.", role: "Boulangerie Artisanale", quote: "Le site Click & Collect a transformé notre activité. +40% de commandes en 3 mois." },
  { name: "Marc T.", role: "Agence immobilière", quote: "Une refonte premium qui inspire enfin confiance à nos prospects." },
  { name: "Inès R.", role: "Salle de sport", quote: "L'équipe Blackline a saisi notre vision dès le premier rendez-vous. Bluffant." },
];

export default function Landing() {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    trackView("/");
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(data.slice(0, 4));
      } catch {}
    })();
  }, []);

  return (
    <div className="bg-[#09090b] text-white">
      <SEO
        title="Blackline Studio — Agence Web Premium"
        description="Sites internet, Click & Collect, prise de rendez-vous et identité visuelle conçus pour les entreprises ambitieuses. Devis gratuit sous 24h."
        keywords="création site internet, agence web premium, click and collect, prise de rendez-vous en ligne, identité visuelle, logo, charte graphique, agence digitale France"
      />
      {/* ===== HERO ===== */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background visuals */}
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/6141905/pexels-photo-6141905.jpeg"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09090b]/70 via-[#09090b]/85 to-[#09090b]" />
          <div className="absolute inset-0 diagonal-lines" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 w-full pt-24 pb-16">
          <motion.div
            initial="hidden" animate="show" variants={fadeUp}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#d4af37]/30 bg-[#d4af37]/5 text-[#d4af37] text-[11px] tracking-[0.2em] uppercase mb-8"
            data-testid="hero-badge"
          >
            <span className="w-1.5 h-1.5 bg-[#d4af37] rounded-full pulse-gold" /> Studio digital premium
          </motion.div>

          <motion.h1
            initial="hidden" animate="show" custom={1} variants={fadeUp}
            className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[1.02] tracking-tight max-w-5xl"
            data-testid="hero-title"
          >
            Des solutions web<br className="hidden sm:block" />
            <span className="italic font-light">professionnelles</span> qui<br className="hidden sm:block" />
            développent <span className="gold-text">votre activité</span>.
          </motion.h1>

          <motion.p
            initial="hidden" animate="show" custom={2} variants={fadeUp}
            className="mt-8 max-w-2xl text-zinc-400 text-lg leading-relaxed font-light"
            data-testid="hero-subtitle"
          >
            Sites internet, Click & Collect, prise de rendez-vous et identité visuelle conçus
            pour les entreprises ambitieuses.
          </motion.p>

          <motion.div
            initial="hidden" animate="show" custom={3} variants={fadeUp}
            className="mt-12 flex flex-col sm:flex-row gap-4"
          >
            <Link to="/realisations" data-testid="hero-cta-realisations"
              className="group inline-flex items-center justify-center gap-3 bg-white text-black px-8 py-4 font-semibold transition-all hover:bg-[#d4af37]">
              Découvrir nos réalisations
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/devis" data-testid="hero-cta-devis"
              className="group inline-flex items-center justify-center gap-3 border border-white/20 hover:border-[#d4af37] px-8 py-4 font-semibold transition-all hover:bg-white/5">
              Demander un devis gratuit
              <ArrowUpRight size={18} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ===== TRUST MARQUEE ===== */}
      <section className="border-y border-white/10 py-8 overflow-hidden bg-[#0a0a0c]">
        <div className="flex gap-16 animate-marquee whitespace-nowrap text-zinc-500 text-sm tracking-[0.25em] uppercase">
          {Array.from({ length: 2 }).map((_, j) => (
            <div key={j} className="flex gap-16 px-8">
              <span>— Apple-inspired design</span>
              <span>— Performance Stripe</span>
              <span>— Animations Framer</span>
              <span>— SEO avancé</span>
              <span>— Core Web Vitals</span>
              <span>— Responsive parfait</span>
              <span>— Click & Collect</span>
              <span>— Prise de RDV</span>
            </div>
          ))}
        </div>
      </section>

      {/* ===== SERVICES — SITES ===== */}
      <section className="py-24 lg:py-32 relative" data-testid="services-sites">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
            <div className="lg:col-span-7">
              <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-4">01 — Création de sites internet</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                Des sites <span className="italic font-light">construits</span> pour convertir.
              </h2>
            </div>
            <p className="lg:col-span-5 text-zinc-400 font-light leading-relaxed">
              De la vitrine professionnelle au Click & Collect avancé, chaque site est conçu
              pour générer des résultats mesurables.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {services.sites.map((s, i) => (
              <motion.div
                key={s.title}
                initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}
                custom={i} variants={fadeUp}
                className="bg-[#09090b] p-8 lg:p-10 card-lift hover:bg-[#101013] group"
                data-testid={`service-site-${i}`}
              >
                <s.icon size={28} className="text-[#d4af37] mb-6" strokeWidth={1.5} />
                <h3 className="font-display text-2xl mb-3 tracking-tight">{s.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed font-light">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SERVICES — IDENTITE VISUELLE ===== */}
      <section className="py-24 lg:py-32 bg-[#0a0a0c]" data-testid="services-identity">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
            <div className="lg:col-span-7">
              <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-4">02 — Identité visuelle</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                Une <span className="italic font-light">marque</span> reconnaissable au premier regard.
              </h2>
            </div>
            <p className="lg:col-span-5 text-zinc-400 font-light leading-relaxed">
              Logo, charte graphique, supports imprimés et digitaux : nous construisons votre
              identité de A à Z.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {services.identite.map((s, i) => (
              <motion.div
                key={s.title}
                initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}
                custom={i} variants={fadeUp}
                className="bg-[#0a0a0c] p-8 lg:p-10 card-lift hover:bg-[#101013]"
                data-testid={`service-identity-${i}`}
              >
                <s.icon size={28} className="text-[#d4af37] mb-6" strokeWidth={1.5} />
                <h3 className="font-display text-2xl mb-3 tracking-tight">{s.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed font-light">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== INDUSTRIES — Solutions métier ===== */}
      <section className="py-24 lg:py-32" data-testid="industries-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
            <div className="lg:col-span-7">
              <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-4">03 — Solutions métier</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                Pensé pour votre <span className="italic font-light">activité</span>.
              </h2>
            </div>
            <p className="lg:col-span-5 text-zinc-400 font-light leading-relaxed">
              Chaque secteur dispose d'une solution clé en main avec sa propre console de gestion.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {industries.map((it, i) => (
              <motion.div
                key={it.name}
                initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}
                custom={i} variants={fadeUp}
                className="bg-[#09090b] p-6 lg:p-8 card-lift hover:bg-[#0f0f12] group cursor-pointer relative"
                data-testid={`industry-${i}`}
              >
                <it.icon size={26} className="text-[#d4af37] mb-5" strokeWidth={1.5} />
                <h3 className="font-display text-xl mb-1.5">{it.name}</h3>
                <p className="text-zinc-500 text-xs leading-relaxed">{it.desc}</p>
                <ArrowUpRight size={16} className="absolute top-6 right-6 text-zinc-700 group-hover:text-[#d4af37] transition-colors" />
              </motion.div>
            ))}
            <div className="bg-[#0f0f12] p-6 lg:p-8 flex items-center justify-center text-center">
              <div>
                <div className="text-xs tracking-[0.2em] text-zinc-500 uppercase mb-2">Votre métier</div>
                <Link to="/devis" className="text-[#d4af37] font-medium text-sm link-gold">Discutons →</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== REALISATIONS Preview ===== */}
      <section className="py-24 lg:py-32 bg-[#0a0a0c]" data-testid="realisations-preview">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
            <div>
              <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-4">Portfolio</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-2xl">
                Nos dernières <span className="italic font-light">réalisations</span>.
              </h2>
            </div>
            <Link to="/realisations" data-testid="realisations-view-all"
              className="text-sm font-medium link-gold inline-flex items-center gap-2 text-white">
              Voir toutes les réalisations <ArrowRight size={16} />
            </Link>
          </div>

          {projects.length === 0 ? (
            <div className="border border-dashed border-white/10 p-16 text-center" data-testid="no-projects">
              <p className="text-zinc-500 font-light">Portfolio en cours de constitution. Bientôt visible ici.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
              {projects.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
                  custom={i} variants={fadeUp}
                  className="bg-[#0a0a0c] card-lift group cursor-pointer"
                  data-testid={`project-preview-${i}`}
                >
                  <div className="aspect-[16/10] overflow-hidden bg-[#18181b] relative">
                    {p.image_url ? (
                      <img src={resolveImg(p.image_url)} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-700 font-display text-5xl">{p.title?.[0]}</div>
                    )}
                  </div>
                  <div className="p-8">
                    <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-3">{p.category}</div>
                    <h3 className="font-display text-2xl mb-3">{p.title}</h3>
                    <p className="text-zinc-400 text-sm font-light line-clamp-2 mb-5">{p.description}</p>
                    <div className="flex gap-4">
                      {p.demo_url && (
                        <a href={p.demo_url} target="_blank" rel="noreferrer"
                          className="text-[#d4af37] text-sm font-medium link-gold inline-flex items-center gap-1.5">
                          Visiter la démo <ArrowUpRight size={14} />
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-24 lg:py-32" data-testid="testimonials-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-4">Témoignages</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight">
              Ils nous ont fait <span className="italic font-light">confiance</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                className="bg-[#0a0a0c] p-8 lg:p-10"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex gap-1 mb-5">
                  {Array.from({ length: 5 }).map((_, k) => <Star key={k} size={14} className="text-[#d4af37] fill-[#d4af37]" />)}
                </div>
                <Quote size={22} className="text-[#d4af37]/40 mb-4" />
                <p className="text-zinc-200 leading-relaxed mb-6 font-light">"{t.quote}"</p>
                <div>
                  <div className="font-medium text-white">{t.name}</div>
                  <div className="text-xs tracking-wider text-zinc-500 uppercase">{t.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROCESS ===== */}
      <section className="py-24 lg:py-32 bg-[#0a0a0c]" data-testid="process-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-4">Méthode</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl mb-16">
            Un processus clair, des résultats <span className="italic font-light">mesurables</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
            {[
              { n: "01", t: "Découverte", d: "Brief, objectifs, audience, contraintes." },
              { n: "02", t: "Conception", d: "Wireframes, identité, prototypes interactifs." },
              { n: "03", t: "Production", d: "Développement premium, SEO, performance." },
              { n: "04", t: "Livraison", d: "Mise en ligne, formation, accompagnement." },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                className="border-t border-[#d4af37]/30 pt-6"
              >
                <div className="font-display text-5xl text-[#d4af37]/40 mb-4">{s.n}</div>
                <h3 className="font-display text-xl mb-2">{s.t}</h3>
                <p className="text-zinc-400 text-sm font-light leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA FINAL ===== */}
      <section className="py-24 lg:py-32 relative overflow-hidden" data-testid="final-cta">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/30232780/pexels-photo-30232780.jpeg" alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09090b] via-[#09090b]/85 to-[#09090b]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] mb-8">
            Prêt à faire <span className="italic font-light">décoller</span> votre activité ?
          </h2>
          <p className="text-zinc-400 text-lg font-light mb-10">
            Échangeons 30 minutes. Devis gratuit, sans engagement.
          </p>
          <Link to="/devis" data-testid="final-cta-button"
            className="inline-flex items-center gap-3 btn-gold px-10 py-5 text-base">
            Demander un devis gratuit <ArrowRight size={18} />
          </Link>
          <div className="mt-8 flex items-center justify-center gap-6 text-xs text-zinc-500">
            <span className="inline-flex items-center gap-2"><Check size={14} className="text-[#d4af37]" /> Réponse sous 24 h</span>
            <span className="inline-flex items-center gap-2"><Check size={14} className="text-[#d4af37]" /> Sans engagement</span>
            <span className="inline-flex items-center gap-2"><Check size={14} className="text-[#d4af37]" /> 100% personnalisé</span>
          </div>
        </div>
      </section>
    </div>
  );
}
